import { FC, useRef } from 'react';
import styled from 'styled-components';

import { SeoTitle } from '#/components/common';
import { SharingModal, SharingModalApi } from '#/components/Modal';
import { PageSpinner } from '#/components/Splash';
import { NotFound } from '#/components/Status';
import devtoolsApi from '#/dev/devtools';
import {
  useActions,
  useDataContext,
  usePageGuard,
  usePaywall,
  useResponsiveFormFactor,
} from '#/hooks';
import { PageRenderer } from '#/pages/PageRenderer';
import { MenuBackgroundStyle } from '#/service/constants';
import { remify } from '#/service/utils';
import { useAuthentication } from '#/store/auth';

import { PageContainerProps, PageControllerProps } from './pageTypes';
import { usePageController } from './usePageController';

/** Controller for Dynamic UI layout based pages/views. */
const PageController: FC<PageControllerProps> = (props) => {
  const { pageId, fullPage = true } = props;
  const formFactor = useResponsiveFormFactor();
  const { getPageContext } = useDataContext();
  const { isShowing: isPaywallShowing } = usePaywall();
  const sharingModalRef = useRef<SharingModalApi>(null);
  const { actions } = useActions(sharingModalRef);
  const {
    layout,
    isPageLoading,
    error,
    theme,
    translations,
    parentalSystem,
    v2Templates,
    defaultContext,
    pageTitle,
  } = usePageController(formFactor, props);
  const { isInitialised: isAuthStoreInitialized } = useAuthentication();
  usePageGuard();

  const templateProps = layout?.templates?.[formFactor]?.properties;
  const hasTopPadding =
    (fullPage && theme.menu.background === MenuBackgroundStyle.Opaque) ||
    !!templateProps?.topContainerShouldNotOverlap;

  if (error) {
    if (error.statusCode === 404) {
      return <NotFound />;
    }

    throw error;
  }

  if (isPageLoading || !layout || isPaywallShowing || !isAuthStoreInitialized) {
    return (
      <PageSpinner $startFadeout={isPageLoading} data-testid="page-spinner" />
    );
  }

  const context = getPageContext(defaultContext, layout);

  console.debug('PageController - Layout with data', layout);
  console.debug('PageController - v2Templates', v2Templates);

  // prepare the params needed for the `buildDynamicUi` function, but don't call it yet
  // see the explanation in `PageRenderer.tsx`
  const buildDynamicUiParams = {
    fontConfig: theme.fonts,
    translations,
    parentalSystem,
    formFactor,
    v2Templates,
  };

  if (process.env.goal === 'local') {
    // update devtools
    devtoolsApi.setLayout(layout);
    devtoolsApi.setContexts({ page: context });
    devtoolsApi.setStoryPageDef({
      storyTitle: 'Untitled story',
      layout,
      context,
      fontConfig: theme.fonts,
      translations,
      parentalSystem,
      formFactor,
      v2Templates,
      theme,
    });
  }

  return (
    <PageContainer $hasTopPadding={hasTopPadding}>
      {pageTitle && <SeoTitle>{pageTitle}</SeoTitle>}
      <PageRenderer
        actions={actions}
        buildDynamicUiParams={buildDynamicUiParams}
        context={context}
        layout={layout}
        pageId={pageId}
      />
      <SharingModal apiRef={sharingModalRef} />
    </PageContainer>
  );
};

PageController.displayName = 'PageController';

export default PageController;

export const EmptyPage = styled.div``;

const PageContainer = styled.div<PageContainerProps>`
  padding-top: ${({ $hasTopPadding }) => ($hasTopPadding ? remify(80) : '0')};
  min-height: ${({ $hasTopPadding }) =>
    $hasTopPadding ? `calc(100vh - ${remify(80)})` : '100vh'};
`;
